import * as React from 'react';

import {useFloating, autoUpdate} from '@floating-ui/react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {offset} from '@floating-ui/core';
import classify from 'src/utils/classify';
import {ClickAway} from '@spaced-out/ui-design-system/lib/utils/click-away';
import {Input} from '@spaced-out/ui-design-system/lib/components/Input';
import {
  formatIsoDate,
  getAddedDate,
  getTodayInTimezone,
} from '@spaced-out/ui-design-system/lib/utils';
import {selectAgencyData} from 'src/selectors/agency';
import {useSelector} from 'react-redux';
import {DateRangePicker} from '@spaced-out/ui-design-system/lib/components/DateRangePicker';
import {
  spaceNone,
  spaceXXSmall,
} from '@spaced-out/ui-design-system/lib/styles/variables/_space';
import {sizeFluid} from '@spaced-out/ui-design-system/lib/styles/variables/_size';
import {mergeRefs} from '@spaced-out/ui-design-system/lib/utils/merge-refs';
import {BodySmall} from '@spaced-out/ui-design-system/lib/components/Text';
import css from './index.css';


type FormikFieldType = any;

type DateRangeProps = {
  selectedDateRange: {
    startDate: string | null | undefined;
    endDate: string | null | undefined;
  };
  setSelectedDateRange: (arg1: {
    startDate: string | null | undefined;
    endDate: string | null | undefined;
  }) => void;
  label: string;
  isRequired: boolean;
  error: string | null | undefined;
  touched: boolean;
  formikField: FormikFieldType;
  classNames?: string;
  placement?: string;
  disabled?: boolean;
};

export const DateRangeComponent = ({
  selectedDateRange,
  setSelectedDateRange,
  label,
  isRequired,
  error,
  touched,
  formikField,
  classNames,
  placement = 'bottom-start',
  disabled = false,
}: DateRangeProps) => {
  const {t, makeKey,language} = useLabelTranslation();
  const {x, y, refs, strategy} = useFloating({
    open: true,
    strategy: 'absolute',
    // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'Placement | undefined'.
    placement: placement || 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [offset(parseInt(spaceXXSmall))],
  });

  // @ts-expect-error - TS7031 - Binding element 'startDate' implicitly has an 'any' type. | TS7031 - Binding element 'endDate' implicitly has an 'any' type.
  const onApplyClick = ({startDate, endDate}) => {
    setSelectedDateRange({
      startDate,
      endDate,
    });
  };

  const startDate = selectedDateRange.startDate
    ? formatIsoDate(selectedDateRange.startDate)
    : 'yyyy.mm.dd';
  const endDate = selectedDateRange.endDate
    ? formatIsoDate(selectedDateRange.endDate)
    : 'yyyy.mm.dd';

  const hasError = Boolean(error && touched);
  const {timezone} = useSelector(selectAgencyData);
  const today = getTodayInTimezone(timezone);

  return (
    <div
      className={classify(css.dropdownInputContainer, classNames)}
      {...formikField}
    >
      <ClickAway>
        {({isOpen = true, onOpen, clickAway, boundaryRef, triggerRef}) => (
          <>
            <Input
              size="small"
              placeholder={t('Select_Date', 'Select Date')}
              boxRef={mergeRefs([refs.setReference, triggerRef])}
              value={
                startDate.replace(/-/g, '.') +
                '  -  ' +
                endDate.replace(/-/g, '.')
              }
              classNames={{box: css.inputBox}}
              iconRightName={isOpen ? 'angle-up' : 'angle-down'}
              readOnly
              onContainerClick={(e) => {
                e.stopPropagation();
                onOpen();
              }}
              error={hasError}
              errorText={error || ''}
              required={isRequired}
              label={label}
              // @ts-expect-error - TS17001 - JSX elements cannot have multiple attributes with the same name.
              classNames={{wrapper: css.inputContainer}}
              disabled={disabled}
            />

            {isOpen && (
              <div
                ref={mergeRefs([refs.setFloating, boundaryRef])}
                style={{
                  position: strategy,
                  top: y ?? spaceNone,
                  left: x ?? spaceNone,
                  width: sizeFluid,
                }}
              >
                <DateRangePicker
                  onApply={(dateRange) => {
                    console.log('dateRange', dateRange);
                    // @ts-expect-error - TS2345 - Argument of type 'DateRangeWithTimezone' is not assignable to parameter of type '{ startDate: any; endDate: any; }'.
                    onApplyClick(dateRange);
                    clickAway();
                  }}
                  onCancel={() => {
                    clickAway();
                  }}
                  selectedDateRange={{
                    ...selectedDateRange,
                  }}
                  classNames={{
                    wrapper: css.dateRangePickerContainer,
                  }}
                  maxDate={getAddedDate(today, 4, 'y')}
                />
              </div>
            )}
          </>
        )}
      </ClickAway>
    </div>
  );
};

type NumberRangeProps = {
  selectedRange: {
    minValue: string | number | null | undefined;
    maxValue: string | number | null | undefined;
  };
  setSelectedRange: (arg1: {
    minValue: string | number | null | undefined;
    maxValue: string | number | null | undefined;
  }) => void;
  label: string;
  error: string | null | undefined;
  isRequired: boolean;
  touched: boolean;
  formikField: FormikFieldType;
  classNames?: string;
  disabled?: boolean;
};

export const NumberRangeComponent = ({
  selectedRange,
  setSelectedRange,
  label,
  error,
  isRequired,
  touched,
  formikField,
  classNames,
  disabled = false,
}: NumberRangeProps) => {
  const {t, makeKey,language} = useLabelTranslation();
  const {minValue, maxValue} = selectedRange;

  const handleBlur = () => {
    formikField.onBlur({target: {name: formikField.name}});
  };

  return (
    <div className={classify(css.numberRangeContainer, classNames)}>
      {label && (
        <label className={css.label}>
          {t(makeKey(label), label)}
          {isRequired && <span className={css.required}>*</span>}
        </label>
      )}
      <div className={css.numberRangeInputs}>
        <Input
          type="number"
          value={String(minValue || '')}
          onChange={(e) => {
            const newMin = e.target.value === '' ? '' : Number(e.target.value);
            setSelectedRange({
              minValue: newMin,
              maxValue,
            });
          }}
          placeholder={t('Min', 'Min')}
          className={css.minInput}
          required={isRequired}
          onBlur={handleBlur}
          disabled={disabled}
        />

        <Input
          type="number"
          value={String(maxValue || '')}
          onChange={(e) => {
            const newMax = e.target.value === '' ? '' : Number(e.target.value);
            setSelectedRange({
              minValue,
              maxValue: newMax,
            });
          }}
          placeholder={t('Max', 'Max')}
          className={css.maxInput}
          required={isRequired}
          onBlur={handleBlur}
          disabled={disabled}
        />
      </div>
      {error && touched && <BodySmall color="danger">{t(makeKey(error), error)}</BodySmall>}
    </div>
  );
};
